import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { DotsSixVerticalIcon, PlusIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TSlidesSidebarProps = {
  slides: TSlide[];
  selectedIndex: number;
  onSelectSlide: (index: number) => void;
  onDeleteSlide: (slideId: string) => void;
  onAddSlide: () => void;
  onReorderSlides: (newOrder: TSlide[]) => void;
  isGenerating: boolean;
  isReadOnly: boolean;
};

type TSortableSlideItemProps = {
  slide: TSlide;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  hasEdit: boolean;
  isReadOnly: boolean;
};

function SortableSlideItem({
  slide,
  index,
  isSelected,
  onSelect,
  onDelete,
  hasEdit,
  isReadOnly
}: TSortableSlideItemProps) {
  const { t } = useTranslation("projectHome");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0
  };

  return (
    <div
      ref={setNodeRef}
      data-testid={`slides-sidebar-${slide.id}-item`}
      style={style}
      onClick={onSelect}
      aria-current={isSelected || undefined}
      className={cn(
        "group/slide relative flex w-full cursor-pointer items-start gap-2 rounded-lg py-2 ps-1.5 pe-1 transition-colors",
        "hover:bg-muted/70 aria-current:bg-primary-50",
        isDragging && "bg-card shadow-lg ring-1 ring-foreground/10"
      )}
    >
      {hasEdit ? (
        <button
          type="button"
          aria-label={t("presentationBuilder.moveSlide", { number: index + 1 })}
          data-testid={`slides-sidebar-${slide.id}-drag-handle`}
          className="mt-0.5 flex h-5 w-3.5 shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover/slide:opacity-100 focus-visible:opacity-100"
          {...attributes}
          {...listeners}
        >
          <DotsSixVerticalIcon className="size-3.5" />
        </button>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      <span
        className={cn(
          "typo-caption flex size-5 shrink-0 items-center justify-center rounded-md tabular-nums",
          isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        )}
      >
        {index + 1}
      </span>
      <span className="typo-label-sm line-clamp-2 min-w-0 flex-1 pt-px text-foreground">
        {slide.title || <span className="text-muted-foreground italic">{t("presentation.untitledSlide")}</span>}
      </span>
      {hasEdit && (
        <Button
          variant="ghost"
          size="icon-xs"
          data-testid={`slides-sidebar-${slide.id}-delete-button`}
          aria-label={t("presentationBuilder.deleteSlide", { number: index + 1 })}
          title={t("presentationBuilder.deleteSlide", { number: index + 1 })}
          disabled={isReadOnly}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/slide:opacity-100 hover:text-destructive focus-visible:opacity-100"
        >
          <TrashSimpleIcon />
        </Button>
      )}
    </div>
  );
}

function DragOverlayItem({ slide, index }: { slide: TSlide; index: number }) {
  const { t } = useTranslation("projectHome");
  return (
    <div className="flex w-56 items-start gap-2 rounded-lg bg-card py-2 ps-1.5 pe-2 shadow-lg ring-1 ring-foreground/10">
      <DotsSixVerticalIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <span className="typo-caption flex size-5 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground tabular-nums">
        {index + 1}
      </span>
      <span className="typo-label-sm line-clamp-2 min-w-0 flex-1 pt-px text-foreground">
        {slide.title || t("presentation.untitledSlide")}
      </span>
    </div>
  );
}

export function SlidesSidebar(props: TSlidesSidebarProps) {
  const { slides, selectedIndex, onSelectSlide, onAddSlide, onDeleteSlide, onReorderSlides, isGenerating, isReadOnly } =
    props;
  const { t } = useTranslation("projectHome");
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const [activeId, setActiveId] = useState<string | null>(null);
  const selectedSlideRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (selectedSlideRef.current && sidebarRef.current) {
      const sidebar = sidebarRef.current;
      const selectedElement = selectedSlideRef.current;

      const sidebarRect = sidebar.getBoundingClientRect();
      const selectedRect = selectedElement.getBoundingClientRect();

      const isVisible = selectedRect.top >= sidebarRect.top && selectedRect.bottom <= sidebarRect.bottom;

      if (!isVisible) {
        selectedElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [selectedIndex]);

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id as string;
    setActiveId(id);
    const dragIndex = slides.findIndex((s) => s.id === id);
    if (dragIndex !== -1) onSelectSlide(dragIndex);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = slides.findIndex((slide) => slide.id === active.id);
    const newIndex = slides.findIndex((slide) => slide.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newSlides = arrayMove(slides, oldIndex, newIndex);
    onReorderSlides(newSlides);

    if (selectedIndex === oldIndex) {
      onSelectSlide(newIndex);
    } else if (oldIndex < selectedIndex && newIndex >= selectedIndex) {
      onSelectSlide(selectedIndex - 1);
    } else if (oldIndex > selectedIndex && newIndex <= selectedIndex) {
      onSelectSlide(selectedIndex + 1);
    }
  }

  const activeSlide = activeId ? slides.find((s) => s.id === activeId) : null;

  const dndList = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-0.5 p-2">
        <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy} disabled={isReadOnly}>
          {slides.map((slide, index) => (
            <div key={slide.id} ref={index === selectedIndex ? selectedSlideRef : undefined}>
              <SortableSlideItem
                slide={slide}
                index={index}
                isSelected={index === selectedIndex}
                onSelect={() => onSelectSlide(index)}
                onDelete={() => onDeleteSlide(slide.id)}
                hasEdit={hasEdit}
                isReadOnly={isReadOnly}
              />
            </div>
          ))}
        </SortableContext>
      </div>
      <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
        {activeSlide ? (
          <DragOverlayItem slide={activeSlide} index={slides.findIndex((s) => s.id === activeSlide.id)} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );

  return (
    <div ref={sidebarRef} data-slot="slides-sidebar" className="flex h-full w-60 min-w-60 flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-foreground/5 ps-4 pe-2">
        <span className="typo-label-md text-foreground">{t("presentation.slides")}</span>
        <Badge variant="secondary" className="h-5 min-w-5 px-1.5 tabular-nums">
          {slides.length}
        </Badge>
        {hasEdit && (
          <Button
            variant="ghost"
            size="icon-sm"
            shape="pill"
            data-testid="slides-sidebar-new-slide-button"
            aria-label={t("presentationBuilder.addSlide")}
            title={t("presentation.addSlide")}
            onClick={onAddSlide}
            disabled={slides.length === 0 || isGenerating || isReadOnly}
            className="ms-auto text-muted-foreground"
          >
            <PlusIcon />
          </Button>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {isGenerating &&
          Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex items-center gap-2 px-4 py-2.5">
              <Skeleton className="size-5 rounded-md" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        {slides.length === 0 && !isGenerating && (
          <p className="typo-caption m-auto px-4 text-center text-muted-foreground">{t("presentation.noSlides")}</p>
        )}
        {slides.length > 0 && !isGenerating && dndList}
      </div>
    </div>
  );
}
